import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { fetchApiKeys, createApiKey, deleteApiKey } from '../store/slices/apiKeysSlice';
import { ApiKey } from '../types';

const ApiKeys: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { apiKeys, loading, error } = useSelector(
    (state: RootState) => state.apiKeys
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [showKeys, setShowKeys] = useState<{ [key: string]: boolean }>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    clientName: '',
    description: '',
    environment: 'production' as 'production' | 'development',
  });

  useEffect(() => {
    dispatch(fetchApiKeys());
  }, [dispatch]);

  const handleCreateClick = () => {
    setFormData({
      clientName: '',
      description: '',
      environment: 'production',
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async () => {
    try {
      const keyData = {
        name: formData.clientName,
        description: formData.description,
        environment: formData.environment
      };
      const result = await dispatch(createApiKey(keyData)).unwrap();
      setNewApiKey(result.apiSecret || 'Generated API Key');
      setDialogOpen(false);
      setSnackbarMessage('APIキーが正常に作成されました');
      setSnackbarOpen(true);
      // Refresh the list
      dispatch(fetchApiKeys());
    } catch (error: any) {
      setSnackbarMessage(`APIキーの作成に失敗しました: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedApiKey) {
      try {
        await dispatch(deleteApiKey(selectedApiKey.id)).unwrap();
        setDeleteDialogOpen(false);
        setSelectedApiKey(null);
        setSnackbarMessage('APIキーが正常に削除されました');
        setSnackbarOpen(true);
        // Refresh the list
        dispatch(fetchApiKeys());
      } catch (error: any) {
        setSnackbarMessage(`APIキーの削除に失敗しました: ${error.message}`);
        setSnackbarOpen(true);
      }
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    setShowKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage('APIキーをクリップボードにコピーしました');
    setSnackbarOpen(true);
  };

  const getEnvironmentChip = (environment: string) => {
    switch (environment) {
      case 'production':
        return <Chip label="本番" color="error" size="small" />;
      case 'development':
        return <Chip label="開発" color="info" size="small" />;
      default:
        return <Chip label={environment} size="small" />;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="有効" color="success" size="small" />;
      case 'inactive':
        return <Chip label="無効" color="default" size="small" />;
      case 'expired':
        return <Chip label="期限切れ" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return key;
    return `${key.substring(0, 4)}${'*'.repeat(key.length - 8)}${key.substring(key.length - 4)}`;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        APIキー管理
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body1" color="text.secondary">
              外部システム連携用のAPIキーを管理します。APIキーには全機能へのアクセス権限が付与されます。
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              新規APIキー作成
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>クライアント名</TableCell>
                <TableCell>説明</TableCell>
                <TableCell>APIキー</TableCell>
                <TableCell>環境</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>作成日時</TableCell>
                <TableCell>最終使用</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (!apiKeys || apiKeys.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (!apiKeys || apiKeys.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    APIキーがありません
                  </TableCell>
                </TableRow>
              ) : (
                apiKeys.map((apiKey: ApiKey) => (
                  <TableRow key={apiKey.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {apiKey.clientName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {apiKey.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontFamily="monospace">
                          {showKeys[apiKey.id] ? (apiKey.apiSecret || '***') : maskApiKey(apiKey.apiSecret || '***')}
                        </Typography>
                        <Tooltip title={showKeys[apiKey.id] ? '非表示' : '表示'}>
                          <IconButton
                            size="small"
                            onClick={() => toggleKeyVisibility(apiKey.id)}
                          >
                            {showKeys[apiKey.id] ? <HideIcon /> : <ViewIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="コピー">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(apiKey.apiSecret || '')}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getEnvironmentChip(apiKey.environment || 'production')}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(apiKey.status)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(apiKey.createdAt).toLocaleString('ja-JP')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {apiKey.lastUsedAt 
                          ? new Date(apiKey.lastUsedAt).toLocaleString('ja-JP')
                          : '未使用'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(apiKey)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create API Key Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          新規APIキー作成
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="クライアント名 *"
              value={formData.clientName}
              onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
              margin="normal"
              required
              helperText="APIキーを使用するクライアント/システムの名前"
            />
            <TextField
              fullWidth
              label="説明"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="このAPIキーの用途や説明"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>環境</InputLabel>
              <Select
                value={formData.environment}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  environment: e.target.value as 'production' | 'development'
                })}
                label="環境"
              >
                <MenuItem value="production">本番環境</MenuItem>
                <MenuItem value="development">開発環境</MenuItem>
              </Select>
            </FormControl>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              作成されるAPIキーには決済・顧客管理・サブスクリプション等の全機能へのアクセス権限が付与されます。
            </Alert>
            <Alert severity="warning" sx={{ mt: 1 }}>
              APIキーは作成後に一度だけ表示されます。必ずコピーして安全な場所に保存してください。
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained"
            disabled={!formData.clientName}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          APIキー削除確認
        </DialogTitle>
        <DialogContent>
          <Typography>
            クライアント「{selectedApiKey?.clientName}」のAPIキーを削除しますか？
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            この操作は取り消せません。このAPIキーを使用している全てのシステムが動作しなくなります。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={!!newApiKey} onClose={() => setNewApiKey(null)}>
        <DialogTitle>
          APIキーが作成されました
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            APIキーが正常に作成されました。以下のAPIキーをコピーして安全な場所に保存してください。
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              APIキー:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              p: 2, 
              bgcolor: 'grey.100',
              borderRadius: 1 
            }}>
              <Typography variant="body2" fontFamily="monospace" sx={{ flexGrow: 1 }}>
                {newApiKey}
              </Typography>
              <Tooltip title="コピー">
                <IconButton
                  onClick={() => copyToClipboard(newApiKey!)}
                  color="primary"
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            このAPIキーは二度と表示されません。必ずコピーして保存してください。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewApiKey(null)} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ApiKeys;